import type { BuildPlan, CommandSpec } from "@webyonetim/contracts";
import { useEffect, useMemo, useState } from "react";

import { Button, Checkbox, SelectField } from "../../components/ui/Controls";
import { AlertBanner, InlineResult, StatusBadge } from "../../components/ui/Feedback";
import { CodeValue } from "../../components/ui/ResponsiveData";
import { ApiError, buildReviewsApi, type BuildReview, type BuildReviewChoice } from "../../lib/api";
import { formatDateTime } from "../../lib/format";

export interface BuildPlanReviewProps {
  readonly onApproved?: (review: BuildReview) => void;
  readonly onRefresh?: () => void;
  readonly review: BuildReview;
}

export function BuildPlanReview({ onApproved, onRefresh, review }: BuildPlanReviewProps) {
  const initialChoiceId = review.reviewRequired
    ? ""
    : (review.choices.find((choice) => choice.recommended)?.choiceId ?? "");
  const [selectedChoiceId, setSelectedChoiceId] = useState(initialChoiceId);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [approved, setApproved] = useState<BuildReview | undefined>(
    review.status === "approved" ? review : undefined,
  );

  useEffect(() => {
    setSelectedChoiceId(
      review.reviewRequired
        ? ""
        : (review.choices.find((choice) => choice.recommended)?.choiceId ?? ""),
    );
    setAcknowledged(false);
    setError(undefined);
    setApproved(review.status === "approved" ? review : undefined);
  }, [review.id, review.revision, review.status, review.reviewRequired, review.choices]);

  const selectedChoice = useMemo(
    () => review.choices.find((choice) => choice.choiceId === selectedChoiceId),
    [review.choices, selectedChoiceId],
  );
  const shownPlan = approved?.approval?.buildPlan ?? selectedChoice?.buildPlan;
  const shownDigest = approved?.approval?.buildPlanDigest ?? selectedChoice?.buildPlanDigest;
  const shownCandidate = selectedChoice?.candidate ?? review.recommendedCandidate;

  const approve = async () => {
    if (selectedChoice === undefined || !acknowledged) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await buildReviewsApi.approve(review.projectId, review.id, {
        expectedRevision: review.revision,
        acknowledged: true,
        choiceId: selectedChoice.choiceId,
      });
      setApproved(result);
      onApproved?.(result);
    } catch (caught: unknown) {
      if (caught instanceof ApiError && caught.problem.status === 409) {
        setError(
          "Bu inceleme başka bir işlemde değişti. Güncel plan yüklenmeden build başlatılmadı.",
        );
        onRefresh?.();
      } else {
        setError(problemMessage(caught));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <article aria-labelledby={`review-${review.id}-heading`} className="card stack">
      <div className="split-row">
        <div>
          <p className="eyebrow">Kaynak ve build güvenlik kapısı</p>
          <h2 id={`review-${review.id}-heading`}>Build planını incele</h2>
        </div>
        <StatusBadge
          label={approved === undefined ? "Onay bekliyor" : "Onaylandı"}
          status={approved === undefined ? "queued" : "succeeded"}
        />
      </div>

      <section aria-labelledby={`review-${review.id}-source`}>
        <h3 id={`review-${review.id}-source`}>Değişmez kaynak</h3>
        <dl className="definition-grid">
          {review.source.kind === "git" ? (
            <>
              <div>
                <dt>Repository</dt>
                <dd>{review.source.repository}</dd>
              </div>
              <div>
                <dt>Ref</dt>
                <dd>
                  <CodeValue>{review.source.ref}</CodeValue>
                </dd>
              </div>
              <div>
                <dt>Commit SHA</dt>
                <dd>
                  <CodeValue>{review.source.commitSha}</CodeValue>
                </dd>
              </div>
            </>
          ) : (
            <div>
              <dt>ZIP SHA-256</dt>
              <dd>
                <CodeValue>{review.source.digest}</CodeValue>
              </dd>
            </div>
          )}
          <div>
            <dt>Yapılandırma sürümü</dt>
            <dd>{review.configVersion}</dd>
          </div>
          <div>
            <dt>Algılama güveni</dt>
            <dd>{review.confidence === "high" ? "Yüksek" : "Orta — açık seçim gerekli"}</dd>
          </div>
        </dl>
      </section>

      {approved === undefined ? (
        <SelectField
          help={
            review.reviewRequired
              ? "Birden fazla olası proje bulundu. Sunucunun hazırladığı kapalı planlardan birini seçin."
              : "Önerilen plan seçildi; isterseniz başka bir algılanmış planı seçebilirsiniz."
          }
          label="Framework ve build planı"
          onChange={(event) => {
            setSelectedChoiceId(event.currentTarget.value);
            setAcknowledged(false);
          }}
          required
          value={selectedChoiceId}
        >
          <option disabled value="">
            Plan seçin
          </option>
          {review.choices.map((choice) => (
            <option key={choice.choiceId} value={choice.choiceId}>
              {choiceLabel(choice)}
            </option>
          ))}
        </SelectField>
      ) : null}

      {shownPlan === undefined || shownDigest === undefined ? (
        <AlertBanner heading="Plan seçimi gerekiyor" tone="warning">
          <p>Build komutları ve güvenlik sınırları gösterilmeden onay verilemez.</p>
        </AlertBanner>
      ) : (
        <PlanDetails
          candidateEvidence={shownCandidate.evidence}
          digest={shownDigest}
          idBase={review.id}
          plan={shownPlan}
        />
      )}

      {error === undefined ? null : (
        <AlertBanner heading="Build planı onaylanamadı" tone="error">
          <p>{error}</p>
        </AlertBanner>
      )}

      {approved === undefined ? (
        <div className="stack stack--tight">
          <Checkbox
            checked={acknowledged}
            disabled={selectedChoice === undefined}
            label="Gösterilen kaynak kimliğini, komut dizisini, kaynak limitlerini ve ağ politikasını inceledim."
            onChange={(event) => {
              setAcknowledged(event.currentTarget.checked);
            }}
          />
          <div className="form-actions">
            <Button
              busy={busy}
              busyLabel="Plan onaylanıyor…"
              disabled={selectedChoice === undefined || !acknowledged}
              onClick={() => {
                void approve();
              }}
            >
              Planı onayla ve build kuyruğuna al
            </Button>
            {onRefresh === undefined ? null : (
              <Button onClick={onRefresh} variant="secondary">
                Güncel planı yükle
              </Button>
            )}
          </div>
        </div>
      ) : (
        <InlineResult tone="success">
          Plan {formatDateTime(approved.approval?.approvedAt ?? approved.updatedAt)} tarihinde
          açıkça onaylandı.
        </InlineResult>
      )}
    </article>
  );
}

function PlanDetails({
  candidateEvidence,
  digest,
  idBase,
  plan,
}: {
  readonly candidateEvidence: readonly string[];
  readonly digest: string;
  readonly idBase: string;
  readonly plan: BuildPlan;
}) {
  return (
    <div className="stack">
      <section aria-labelledby={`plan-${idBase}-identity`}>
        <h3 id={`plan-${idBase}-identity`}>Çalıştırılacak sabit plan</h3>
        <dl className="definition-grid">
          <div>
            <dt>Framework</dt>
            <dd>{frameworkLabel(plan.framework)}</dd>
          </div>
          <div>
            <dt>Şablon</dt>
            <dd>
              <CodeValue>{plan.templateId}</CodeValue>
            </dd>
          </div>
          <div>
            <dt>Proje kökü</dt>
            <dd>
              <CodeValue>{plan.projectRoot}</CodeValue>
            </dd>
          </div>
          <div>
            <dt>Plan SHA-256</dt>
            <dd>
              <CodeValue>{digest}</CodeValue>
            </dd>
          </div>
          <div>
            <dt>Çıktı</dt>
            <dd>
              {plan.output.kind === "image" ? "Container image" : "Statik paket"}
              {plan.output.directory === undefined ? null : (
                <>
                  {" · "}
                  <CodeValue>{plan.output.directory}</CodeValue>
                </>
              )}
            </dd>
          </div>
          <div>
            <dt>Çalıştırma kullanıcısı</dt>
            <dd>Root olmayan kullanıcı</dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby={`plan-${idBase}-commands`}>
        <h3 id={`plan-${idBase}-commands`}>Komut ve argv</h3>
        <ol className="command-list">
          {plan.commands.map((command, index) => (
            <li key={`${command.executable}-${String(index)}`}>
              <CommandValue command={command} />
            </li>
          ))}
          {plan.start === undefined ? null : (
            <li>
              <span className="muted">Başlatma: </span>
              <CommandValue command={plan.start} />
            </li>
          )}
        </ol>
        <p className="muted">Her argv öğesi ayrı gönderilir; shell komut metni çalıştırılmaz.</p>
      </section>

      <section aria-labelledby={`plan-${idBase}-limits`}>
        <h3 id={`plan-${idBase}-limits`}>Zorunlu kaynak limitleri</h3>
        <dl className="definition-grid">
          <div>
            <dt>CPU</dt>
            <dd>{plan.limits.cpuMillis} millicore</dd>
          </div>
          <div>
            <dt>Bellek</dt>
            <dd>{formatBytes(plan.limits.memoryBytes)}</dd>
          </div>
          <div>
            <dt>PID</dt>
            <dd>{plan.limits.pids}</dd>
          </div>
          <div>
            <dt>Disk</dt>
            <dd>{formatBytes(plan.limits.diskBytes)}</dd>
          </div>
          <div>
            <dt>Süre</dt>
            <dd>{plan.limits.timeoutSeconds} saniye</dd>
          </div>
          <div>
            <dt>Log</dt>
            <dd>{formatBytes(plan.limits.maxLogBytes)}</dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby={`plan-${idBase}-security`}>
        <h3 id={`plan-${idBase}-security`}>Güvenlik duruşu</h3>
        <dl className="definition-grid">
          <div>
            <dt>Dependency ağı</dt>
            <dd>Yalnız kayıt defterleri</dd>
          </div>
          <div>
            <dt>Build ağı</dt>
            <dd>Kapalı</dd>
          </div>
          <div>
            <dt>İzinli registry hostları</dt>
            <dd>{plan.network.allowedRegistryHosts.join(", ") || "Yok"}</dd>
          </div>
          <div>
            <dt>Kaynak mount</dt>
            <dd>Salt okunur</dd>
          </div>
          <div>
            <dt>Çıktı mount</dt>
            <dd>Yazılabilir, izole</dd>
          </div>
          <div>
            <dt>Build sırları</dt>
            <dd>Yok</dd>
          </div>
          <div>
            <dt>Linux capability</dt>
            <dd>Yok</dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby={`plan-${idBase}-evidence`}>
        <h3 id={`plan-${idBase}-evidence`}>Algılama kanıtı</h3>
        <ul>
          {candidateEvidence.map((evidence) => (
            <li key={evidence}>{evidence}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function CommandValue({ command }: { readonly command: CommandSpec }) {
  return (
    <span className="command-value">
      <CodeValue>{command.executable}</CodeValue>
      {command.args.map((argument, index) => (
        <CodeValue key={`${String(index)}-${argument}`}>{argument}</CodeValue>
      ))}
    </span>
  );
}

function choiceLabel(choice: BuildReviewChoice): string {
  const scripts = [
    choice.override.buildScript === undefined ? undefined : `build:${choice.override.buildScript}`,
    choice.override.startScript === undefined ? undefined : `start:${choice.override.startScript}`,
    choice.override.outputDirectory === undefined
      ? undefined
      : `çıktı:${choice.override.outputDirectory}`,
  ]
    .filter((value): value is string => value !== undefined)
    .join(" · ");
  return `${choice.recommended ? "Önerilen · " : ""}${frameworkLabel(choice.candidate.framework)} · ${choice.candidate.projectRoot} · ${choice.candidate.packageManager}${scripts === "" ? "" : ` · ${scripts}`}`;
}

function frameworkLabel(framework: BuildPlan["framework"]): string {
  if (framework === "nextjs") return "Next.js";
  if (framework === "node") return "Node.js";
  if (framework === "react") return "React";
  return "Vue";
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${formatNumber(bytes / 1024 ** 3)} GiB`;
  if (bytes >= 1024 ** 2) return `${formatNumber(bytes / 1024 ** 2)} MiB`;
  return `${formatNumber(bytes / 1024)} KiB`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(value);
}

function problemMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return `${error.problem.detail ?? error.problem.title} İstek kimliği: ${error.problem.requestId}`;
  }
  return error instanceof Error
    ? error.message
    : "Build planı onaylanamadı. Bağlantınızı kontrol edip yeniden deneyin.";
}
